import {
  IconAvatar,
  IconChat,
  IconLogout,
  IconMessages,
  IconMyAds,
  IconPassword,
  IconRank,
  IconSafe,
  IconSearch,
  IconSystemMessages,
  IconTrash,
  IconUserDeactivate,
  IconUserReactivate,
  IconPreferences,
} from '@/components/ColorIcon/icons';
import { IconType } from '@/components/Icon';
import { ModalState } from '@/hooks/useModal';
import {
  AccountState,
  getSystemNotifications,
  logout,
} from '@/resource/account';
import { getOwnAds } from '@/resource/ad';
import { getConversations } from '@/resource/conversation';
import { getFavorites } from '@/resource/favorites';
import { SettingsType } from '@/resource/settings';
import { partition } from '@/util/partition';
import { RouteType } from '@/util/routeToPath';
import { MouseEventHandler, ReactNode } from 'react';
import { QueryClient } from 'react-query';

export type AccountItemType = {
  title: string;
  description: string;
  icon: ReactNode;
  to: RouteType;
  unseenCount?: number;
  prefetchQuery?: () => void;
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
  disabled?: boolean;
};

export type AccountSectionType = {
  icon: IconType;
  title: string;
  items: AccountItemType[];
};

export function getAccountSections(
  queryClient: QueryClient,
  settings: SettingsType,
  { unreadMessages, unseenNotificationCount, profile }: AccountState,
  modal: ModalState
): AccountSectionType[] {
  const deactivated = profile?.deactivated || false;
  const [nhMessages, profileMessages] = partition(
    unreadMessages,
    (msg) => msg.ad_id === settings.contact_ad?.data.id
  );

  const sections: AccountSectionType[] = [
    {
      icon: 'ad',
      title: 'Vraag & aanbod',
      items: [
        {
          title: 'Mijn berichten',
          description: 'Bekijk en beheer je gesprekken op één plek.',
          icon: <IconMessages />,
          to: { type: 'conversations' },
          unseenCount: deactivated ? 0 : profileMessages.length,
          disabled: deactivated,
          prefetchQuery() {
            queryClient.prefetchQuery({
              queryKey: ['conversations'],
              queryFn: () => getConversations(),
            });
          },
        },
        {
          title: 'Mijn advertenties',
          description: 'Beheer en bewerk je advertenties in een handomdraai.',
          icon: <IconMyAds />,
          to: { type: 'profile_ads' },
          disabled: deactivated,
          prefetchQuery() {
            queryClient.prefetchQuery({
              queryKey: ['profile_ads'],
              queryFn: () => getOwnAds(),
            });
          },
        },
        {
          title: 'Mijn favorieten',
          description: 'Bewaar je favorieten voor snelle toegang.',
          icon: <IconSafe />,
          to: { type: 'saved_ads' },
          disabled: deactivated,
          prefetchQuery() {
            queryClient.prefetchQuery({
              queryKey: ['favorites'],
              queryFn: () => getFavorites(),
            });
          },
        },
        {
          title: 'Mijn zoekopdrachten',
          description: 'Volg je recente zoekacties en ontvang meldingen.',
          disabled: deactivated,
          icon: <IconSearch />,
          to: { type: 'saved_searches' },
        },
      ],
    },
    {
      icon: 'avatar',
      title: 'Mijn profiel',
      items: [
        {
          title: 'Mijn gegevens',
          description: 'Beheer je persoonlijke informatie en instellingen.',

          icon: <IconAvatar />,
          to: { type: 'profile_info' },
        },
        {
          title: 'Chat',
          description:
            'Start een gesprek met onze redactie voor vragen en hulp.',
          icon: <IconChat />,
          to: { type: 'nh_chat' },
          unseenCount: deactivated ? 0 : nhMessages.length,
          disabled: deactivated,
        },
        {
          title: 'Voorkeuren',
          description: 'Geef aan hoe je op de hoogte gehouden wilt worden.',
          icon: <IconPreferences />,
          to: { type: 'profile_preferences' },
        },
        {
          title: `Profiel ${deactivated ? 'heractiveren' : 'deactiveren'}`,
          description: deactivated
            ? 'Heractiveer je profiel als je weer wil deelnemen aan NH Helpt.'
            : 'Deactiveer je profiel als je tijdelijk een pauze van NH wilt nemen.',
          icon: deactivated ? <IconUserReactivate /> : <IconUserDeactivate />,
          to: { type: 'profile_status' },
        },
        {
          title: 'Mijn ranking',
          description: 'lorem ipsum mijn ranking',
          icon: <IconRank />,
          to: { type: 'home' },
          disabled: true,
        },
      ],
    },
    {
      icon: 'gear',
      title: 'Account',
      items: [
        {
          title: 'Systeemmeldingen',
          description: 'Ontvang belangrijke berichten en updates.',
          icon: <IconSystemMessages />,
          to: { type: 'system_notifications' },
          unseenCount: deactivated ? 0 : unseenNotificationCount,
          disabled: deactivated,
          prefetchQuery() {
            queryClient.prefetchQuery({
              queryKey: ['system_notifications'],
              queryFn: () => getSystemNotifications(),
            });
          },
        },
        {
          title: 'Beveiliging',
          description: 'Beheer je toegangscode voor extra veiligheid.',
          icon: <IconPassword />,
          to: { type: 'profile_security' },
        },
        {
          title: 'Account verwijderen',
          description: 'Verwijder je account voorgoed van Jouw NH.',
          icon: <IconTrash />,
          to: { type: 'profile_delete' },
        },
        {
          title: 'Uitloggen',
          description: 'Beëindig je sessie en verlaat je account.',
          icon: <IconLogout />,
          to: { type: 'home' },
          onClick(e) {
            e.preventDefault();
            modal.open(
              {
                type: 'confirm',
                maxWidth: 40,
                props: {
                  title: 'Weet je zeker dat je wilt uitloggen?',
                  onConfirm() {
                    modal.close();
                    logout();
                  },
                  onCancel() {
                    modal.close();
                  },
                },
              },
              () => {
                //
              }
            );
          },
        },
      ],
    },
  ];

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.disabled),
    }))
    .filter((section) => section.items.length > 0);
}
